import React, { useEffect, useState } from 'react';
import { ApplicationFormTemplate } from '../../../../../entities/applicant_tracking/ApplicationFormTemplate';
import { SurveySection } from '../../../../../entities/survey_engine/SurveySection';
import { ApplicationFormSection } from './ApplicationFormSection';
import { ApplicationFormApplyButton } from './ApplicationFormApplyButton';
import { ApplicationFormCandidateConsent } from './ApplicationFormCandidateConsent';
import { useForm } from 'react-hook-form';
import { Source } from '../../../../../entities/applicant_tracking/Source';
import { PreviewJobPost } from '../../../../../entities/applicant_tracking/PreviewJobPost';
import { QuestionAnswer } from '../../../../../services/applicant_tracking/CareersService/SubmitApplicationDto';
import { SingleAttachmentInputFormField } from '../../../../../components/SingleAttachmentInputFormField';
import { ResumeParserInputBody } from '../../../../../components/SingleAttachmentInputFormField/ResumeParserInputBody';
import { CareersService } from '../../../../../services/applicant_tracking/CareersService';
import { ParsedResumesService } from '../../../../../services/v1/applicant_tracking/ParsedResumesService';
import { AlertObject, AlertType } from '../../../../../components/Alert';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { parseJobBoardParams } from '../../../../../utils/applicant_tracking/unified_job_boards/vonq/parse_careers_query_params';
import { CompanyInfo } from '../../../../../entities/CompanyInfo';
import ahoy from 'ahoy.js';
import { useFeatureFlags } from '../../../../../contexts/FeatureFlagContext';
import { CareerAnalyticsEventType } from '../../../../../enums/ClickEvents/CareerAnalyticsEventType';
import { JobPostService } from '../../../../../services/applicant_tracking/JobPostService';
import { ApplicationFormTemplateService } from '../../../../../services/applicant_tracking/ApplicationFormTemplateService';
import { JobPostSettings } from '../../../../../entities/v1/applicant_tracking/JobPostSettings';

const SUCCESS_URL = location.pathname + '/submit/success';
const ERROR_URL = location.pathname + '/submit/error';

const MAX_WIDTH = '600px';

interface PropTypes {
  applicationFormTemplateConsent?: boolean;
  companyInfo?: CompanyInfo;
  previewMode: boolean;
  previewJobPost: PreviewJobPost;
  setAlertObject: (ao: AlertObject) => void;
}

type ResumeDocument = { id?: number; content: string; filename: string };
type OtherDocument = { content: string; filename: string; size?: number };
type Location = {
  name: string;
  address: string;
  city: string;
  state: string;
  country: string;
};

export interface SubmitApplicationFormDto {
  parserResumeContent: string;
  parserResumeFilename: string;
  controllerFileName: string;
  atsApplicationFormName: string;
  atsApplicationFormEmail: string;
  phoneNumber?: string;
  atsApplicationFormLinkedinUrl?: string;
  atsApplicationFormResume?: ResumeDocument;
  atsApplicationDocuments?: OtherDocument[];
  atsApplicationFormCoverLetter?: OtherDocument;
  atsApplicationFormPortfolioUrl: string;
  salaryExpectation: string;
  salaryCurrency: string;
  candidateConsented: boolean;
  sourceId?: number;
  hasWorkingVisa: boolean;
  location?: Location;
  academicDegree?: string;
  yearsExperience?: string;
  atsApplicationFormCompany?: string;
  atsApplicationFormJobTitle?: string;
  customQuestionAnswers: QuestionAnswer[];
}

async function loadApplicationFormTemplate(
  previewMode: boolean,
  jobPostId: number,
  applicationFormTemplateId: number,
  organizationSubdomain: string,
): Promise<ApplicationFormTemplate> {
  if (previewMode) {
    // We should reach out to our private URL (preview on create/edit job flow)
    return await ApplicationFormTemplateService.get(applicationFormTemplateId);
  } else {
    // We should reach out to our public URL (application form tab of job post on careers page)
    return await CareersService.getApplicationForm(
      organizationSubdomain,
      jobPostId,
    );
  }
}

async function loadJobPostSettings(
  previewMode: boolean,
  jobPostId: number,
  organizationSubdomain: string,
): Promise<JobPostSettings> {
  if (previewMode) {
    // We should reach out to our private URL (preview on create/edit job flow)
    return await JobPostService.settings(jobPostId);
  } else {
    // We should reach out to our public URL (application form tab of job post on careers page)
    return await CareersService.getJobPostSettings(
      organizationSubdomain,
      jobPostId,
    );
  }
}

export function ApplicationFormTab(props: PropTypes) {
  const [isLoading, setIsLoading] = useState(true);
  const [isParsingResume, setIsParsingResume] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [applicationFormTemplate, setApplicationFormTemplate] =
    useState<ApplicationFormTemplate>(null);
  const [jobPostSettings, setJobPostSettings] = useState<JobPostSettings>(null);
  const [sources, setSources] = useState<Source[]>([]);

  const consentEnabled: boolean =
    props.applicationFormTemplateConsent ??
    applicationFormTemplate?.candidateConsentEnabled;

  const sourceParams = parseJobBoardParams();

  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    resetField,
    watch,
    formState,
    trigger,
  } = useForm<SubmitApplicationFormDto>({
    mode: 'onTouched',
  });

  useEffect(() => {
    const fetchData = async () => {
      Promise.all([
        await loadApplicationFormTemplate(
          props.previewMode,
          props.previewJobPost.jobPostId,
          props.previewJobPost.applicationFormTemplateId,
          props.companyInfo.subdomain,
        ),
        await loadJobPostSettings(
          props.previewMode,
          props.previewJobPost.jobPostId,
          props.companyInfo.subdomain,
        ),
      ])
        .then(([aft, jps]) => {
          setApplicationFormTemplate(aft);
          setJobPostSettings(jps);
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    setIsLoading(true);
    fetchData();
  }, [
    props.previewJobPost.jobPostId,
    props.previewJobPost.applicationFormTemplateId,
  ]);

  useEffect(() => {
    setIsLoading(true);

    CareersService.getSources(props.companyInfo.subdomain)
      .then(setSources)
      .finally(() => setIsLoading(false));
  }, [props.previewJobPost.jobPostId]);

  const { atsCaptureEventsAhoy } = useFeatureFlags();

  const mapCoverLetter = (cl?: OtherDocument) => {
    if (cl == null) {
      return null;
    }

    return {
      filename: cl.filename,
      content: cl.content,
    };
  };

  const onSubmit = handleSubmit(async (data: SubmitApplicationFormDto) => {
    try {
      if (atsCaptureEventsAhoy) {
        ahoy.track(CareerAnalyticsEventType.ApplicationFormApplyNowClicked, {
          organization_id: props.companyInfo.organizationId,
          applicant_tracking_job_post_id: props.previewJobPost.jobPostId,
        });
      }

      setIsSubmitting(true);

      await CareersService.submitApplication(
        props.companyInfo?.subdomain,
        props.previewJobPost.jobPostId,
        {
          name: data.atsApplicationFormName,
          email: data.atsApplicationFormEmail,
          phoneNumber: data.phoneNumber,
          linkedinUrl: data.atsApplicationFormLinkedinUrl,
          resume: data.atsApplicationFormResume,
          coverLetter: mapCoverLetter(data.atsApplicationFormCoverLetter),
          documents: data.atsApplicationDocuments?.map(
            (document: OtherDocument) => {
              return {
                filename: document.filename,
                content: document.content,
              };
            },
          ),
          portfolioUrl: data.atsApplicationFormPortfolioUrl,
          salaryExpectation: data.salaryExpectation,
          salaryCurrency: data.salaryCurrency,
          candidateConsented: data.candidateConsented,
          sourceId: data.sourceId,
          slSource: sourceParams?.source,
          externalJobBoardCampaignId: sourceParams?.campaign,
          externalJobBoardProvider: sourceParams?.provider,
          hasWorkingVisa: data.hasWorkingVisa,
          address: data.location?.address,
          city: data.location?.city,
          state: data.location?.state,
          country: data.location?.country,
          academicDegree: data.academicDegree,
          yearsExperience: data.yearsExperience,
          company: data.atsApplicationFormCompany,
          jobTitle: data.atsApplicationFormJobTitle,
          customQuestionAnswers: data.customQuestionAnswers,
        },
      );

      window.location.href = SUCCESS_URL;
    } catch (e) {
      console.error(e);
      if (e?.status === 409) {
        return (window.location.href = SUCCESS_URL);
      }

      window.location.href = ERROR_URL;
    } finally {
      setIsSubmitting(false);
    }
  });

  const watchParserResumeContent = watch('parserResumeContent');
  const watchResumeContent = watch('atsApplicationFormResume.content');

  useEffect(() => {
    if (!watchParserResumeContent) return;

    setIsParsingResume(true);

    setValue(
      'atsApplicationFormResume.content',
      getValues('parserResumeContent'),
    );
    setValue(
      'atsApplicationFormResume.filename',
      getValues('parserResumeFilename'),
    );

    ParsedResumesService.create({
      base64File: watchParserResumeContent,
      filename: getValues('parserResumeFilename'),
      subdomain: props.companyInfo?.subdomain,
    })
      .then((parsedResume) => {
        setValue(
          'atsApplicationFormName',
          `${parsedResume.firstName} ${parsedResume.lastName}`,
        );
        setValue('phoneNumber', parsedResume.phone);
        setValue('atsApplicationFormEmail', parsedResume.email);
        setValue('atsApplicationFormLinkedinUrl', parsedResume.linkedinUrl);

        setValue('academicDegree', parsedResume.educationLevel);
        setValue('atsApplicationFormCompany', parsedResume.currentCompany);
        setValue('atsApplicationFormJobTitle', parsedResume.jobTitle);
        setValue('yearsExperience', parsedResume.yearsExperience);
        setValue('atsApplicationFormPortfolioUrl', parsedResume.portfolioUrl);

        setValue('location', {
          country: parsedResume.locationCountryCode,
          state: parsedResume.locationStateCode,
          city: parsedResume.locationCity,
          address: null,
          name: parsedResume.locationName,
        });

        setValue(
          'atsApplicationFormResume.id',
          parsedResume.applicationResumeId,
          {
            shouldValidate: true,
          },
        );

        props.setAlertObject({
          type: AlertType.Success,
          message: (
            <span>
              <b>Autofill completed!</b> Some fields have been filled in.
            </span>
          ),
        });
      })
      .catch((_e: any) =>
        props.setAlertObject({
          type: AlertType.Danger,
          message: (
            <span>
              <b>We were unable to parse the resume!</b> Please try again.
            </span>
          ),
        }),
      )
      .finally(() => setIsParsingResume(false));
  }, [watchParserResumeContent]);

  useEffect(() => {
    setValue('atsApplicationFormResume.id', null);
  }, [watchResumeContent]);

  if (isLoading || !applicationFormTemplate) {
    return <LoadingSpinner showBackdrop />;
  }

  return (
    <>
      {(isParsingResume || isSubmitting) && <LoadingSpinner showBackdrop />}
      <form
        onSubmit={onSubmit}
        className='text-start mx-auto px-3'
        style={{ maxWidth: MAX_WIDTH }}
      >
        <div className='mb-5'>
          {jobPostSettings.enableApplicationFormAutofill && (
            <div className='pb-4'>
              <SingleAttachmentInputFormField<SubmitApplicationFormDto>
                getValues={getValues}
                setValue={setValue}
                formRegister={register}
                headerClassNames='fw-semibold'
                controllerContentName='parserResumeContent'
                controllerFileName='parserResumeFilename'
                fieldName={'Resume / Curriculum Vitae'}
                hideHeader={true}
                allowedFormats={['.pdf', '.doc', '.docx']}
                maxFileSizeMb={10}
                isRequired={false}
                disabled={props.previewMode}
                Body={ResumeParserInputBody}
              />
            </div>
          )}

          {applicationFormTemplate.template.sections.map(
            (section: SurveySection) => (
              <ApplicationFormSection
                key={section.id}
                section={section}
                previewMode={props.previewMode}
                previewJobPost={props.previewJobPost}
                formState={formState}
                sources={sources}
                formRegister={register}
                getValues={getValues}
                setValue={setValue}
                control={control}
                formWatch={watch}
                formResetField={resetField}
                disableSource={sourceParams != null}
                errors={formState.errors}
              />
            ),
          )}

          <ApplicationFormCandidateConsent
            candidateConsentEnabled={consentEnabled}
            previewMode={props.previewMode}
            candidateConsentHtml={jobPostSettings.candidateConsentHtml}
            formRegister={register}
            reValidate={trigger}
          />

          <ApplicationFormApplyButton
            previewMode={props.previewMode}
            disabled={!formState.isValid}
          />
        </div>
      </form>
    </>
  );
}
